import {
  MetricData,
  RowsData,
  SeriesRowsData,
  valueAmount,
  valueUnitKey,
  ValueType,
} from '../types/insights.types'
import { InsightsColourMap, resolveColour } from './colourResolver'

// Pure transforms from the wire `MetricData` shapes into the inputs the Lemonade
// chart primitives + legend expect. Kept separate from the components so the
// mapping (which drives parity with the hand-crafted charts) is unit-testable.

/** Recharts uses a reserved row key for the category axis label. */
export const CATEGORY_KEY = '__category'
// Holds the backend-supplied row total on a chart data record, so a stacked chart's
// tooltip can show the authoritative bucket total (not a client-side sum of the shown
// series, which under a topN window would understate it). Never a real series key.
export const TOTAL_KEY = '__total'

export type SeriesView = { name: string; label: string; color: string }
export type LegendItem = {
  key: string
  label: string
  color: string
  /**
   * Render de-emphasised: the series is listed but not plotted (its unit had no
   * axis — see {@link SeriesAxisAssignment.omitted}).
   */
  muted?: boolean
  /** Pre-translated hover copy, e.g. why a muted series isn't plotted. */
  tooltip?: string
}

/** True when a metric has something to render (a scalar always does; breakdowns need rows). */
export const metricHasData = (data: MetricData): boolean =>
  data.type === 'scalar' ? true : data.rows.length > 0

/**
 * The display name for a breakdown's row axis — the table's key-column heading, a
 * bar/donut's category name, a timeseries' x-axis title. `undefined` for a scalar,
 * for a breakdown the backend couldn't name (composite or ungrouped grouping), and
 * for a blank label: the axis passes through the model on the insights-ai path, and
 * a whitespace-only caption is worse than none. Never derived from `rowAxis.key` —
 * that's a machine id, and if the backend could name the axis it did.
 */
export const rowAxisLabel = (data: MetricData): string | undefined => {
  if (data.type === 'scalar') return undefined

  const label = data.rowAxis?.label

  // Typed as a string, but this is wire data (and the insights-ai path routes it
  // through the model), so a non-string here must caption nothing rather than throw.
  return typeof label === 'string' && label.trim() ? label : undefined
}

/** Host-supplied display-label overrides for a breakdown's series and/or rows. */
export type LabelOverrides = {
  /** series key → replacement label. */
  seriesLabels?: Record<string, string>
  /** row key → replacement label. */
  rowLabels?: Record<string, string>
}

/**
 * Returns `data` with series/row labels swapped for host overrides (keyed by the
 * series/row `key`), leaving everything else untouched. Because every chart body
 * and the legend read labels straight off the data, overriding here is the single
 * place that re-labels a breakdown everywhere it appears. A scalar (or an empty
 * override) is returned unchanged so the reference stays stable for downstream
 * memoisation. An override whose key isn't present in the response is ignored.
 */
export const applyLabelOverrides = (
  data: MetricData,
  { seriesLabels, rowLabels }: LabelOverrides = {}
): MetricData => {
  const hasSeries = seriesLabels && Object.keys(seriesLabels).length > 0
  const hasRows = rowLabels && Object.keys(rowLabels).length > 0

  if (data.type === 'scalar' || (!hasSeries && !hasRows)) return data

  const relabelRow = <T extends { key: string; label: string }>(row: T): T =>
    hasRows && rowLabels[row.key] !== undefined
      ? { ...row, label: rowLabels[row.key] }
      : row

  if (data.type === 'rows') {
    return { ...data, rows: data.rows.map(relabelRow) }
  }

  return {
    ...data,
    series: hasSeries
      ? data.series.map((series) =>
          seriesLabels[series.key] !== undefined
            ? { ...series, label: seriesLabels[series.key] }
            : series
        )
      : data.series,
    rows: data.rows.map(relabelRow),
  }
}

// ── seriesRows (stacked bar) ────────────────────────────────────────────────

/** The full declared series, mapped to chart series descriptors with resolved colours. */
export const seriesViews = (
  data: SeriesRowsData,
  colourMap: InsightsColourMap
): SeriesView[] =>
  data.series.map((series, index) => ({
    name: series.key,
    label: series.label,
    color: resolveColour(series.key, index, colourMap),
  }))

/**
 * Stable-sorts items by a canonical key order (from `<Insights.Domain seriesOrder>`):
 * keys present in `order` come first in that order; the rest keep their relative
 * order after. Keeps series ordering identical across every chart in a domain.
 */
export const orderByKeys = <T>(
  items: readonly T[],
  order: readonly string[],
  keyOf: (item: T) => string
): T[] => {
  if (order.length === 0) return [...items]

  const rank = new Map(order.map((key, index) => [key, index]))
  const rankOf = (item: T) => rank.get(keyOf(item)) ?? Number.MAX_SAFE_INTEGER

  return [...items].sort((a, b) => rankOf(a) - rankOf(b))
}

/** Series keys that carry a non-zero value in at least one row (drives the legend). */
export const presentSeriesKeys = (data: SeriesRowsData): Set<string> => {
  const present = new Set<string>()

  data.rows.forEach((row) =>
    row.values.forEach((value) => {
      if (valueAmount(value.value) > 0) present.add(value.series)
    })
  )

  return present
}

/** One chart row per breakdown row: the category label plus a numeric value per series. */
export const seriesChartData = (
  data: SeriesRowsData
): Record<string, unknown>[] =>
  data.rows.map((row) => {
    const record: Record<string, unknown> = { [CATEGORY_KEY]: row.label }

    data.series.forEach((series) => {
      record[series.key] = valueAmount(
        row.values.find((value) => value.series === series.key)?.value ?? 0
      )
    })

    return record
  })

const firstSeriesValue = (data: SeriesRowsData): ValueType | undefined =>
  data.rows[0]?.values[0]?.value

// ── timeseries (line) ────────────────────────────────────────────────────────

/** A sample value for a series (first row that carries it) — to derive its unit. */
export const seriesSampleValue = (
  data: SeriesRowsData,
  seriesKey: string
): ValueType | undefined => {
  for (const row of data.rows) {
    const match = row.values.find((value) => value.series === seriesKey)

    if (match) return match.value
  }

  return undefined
}

/** Which Y-axis a series is plotted against. */
export type SeriesAxis = 'left' | 'right'

export type SeriesAxisAssignment = {
  /** The axis each plotted series draws against; a series absent here isn't plotted. */
  axisByName: Record<string, SeriesAxis>
  /**
   * Series left unplotted because their unit had no axis to go on (three-plus
   * distinct units in one breakdown). Surfaced in the legend rather than dropped
   * silently — see `axisAwareLegendItems`.
   */
  omitted: string[]
}

/**
 * Series keys grouped by unit, largest group first. `Array.sort` is stable, so
 * equally-sized groups keep their declared order — the first-declared unit wins
 * the left axis on a tie. Keys with no unit (a series the response declares but
 * carries no value for) are excluded: they have no magnitude to scale, so they
 * must not create a unit group of their own.
 */
const unitGroups = (
  seriesKeys: readonly string[],
  unitOf: (key: string) => string | undefined
): string[][] => {
  const groups = new Map<string, string[]>()

  seriesKeys.forEach((key) => {
    const unit = unitOf(key)

    if (unit === undefined) return

    const group = groups.get(unit)

    if (group) group.push(key)
    else groups.set(unit, [key])
  })

  return [...groups.values()].sort((a, b) => b.length - a.length)
}

/**
 * Assigns each series to a Y-axis so a count, a money amount and a duration never
 * share one scale. An explicit `rightAxisSeries` list wins outright (nothing is
 * omitted — the caller owns the split). Otherwise series are grouped by unit
 * (`unitOf(key)` returns a series' unit token, see `valueUnitKey`): the largest
 * group takes the left axis, the next-largest the right.
 *
 * There are only two axes, so with three-plus distinct units the remaining series
 * are reported as {@link SeriesAxisAssignment.omitted} rather than crowded onto an
 * axis whose ticks would then be formatted in someone else's unit. A series with
 * no unit (declared but valueless — e.g. a taxonomy series absent for the active
 * filter, mounted at zero) is pinned left: it renders nothing either way.
 */
export const assignSeriesAxes = (
  seriesKeys: readonly string[],
  unitOf: (key: string) => string | undefined,
  rightAxisSeries?: readonly string[]
): SeriesAxisAssignment => {
  const axisByName: Record<string, SeriesAxis> = {}

  if (rightAxisSeries) {
    seriesKeys.forEach((key) => {
      axisByName[key] = rightAxisSeries.includes(key) ? 'right' : 'left'
    })

    return { axisByName, omitted: [] }
  }

  const [left = [], right = []] = unitGroups(seriesKeys, unitOf)

  left.forEach((key) => {
    axisByName[key] = 'left'
  })
  right.forEach((key) => {
    axisByName[key] = 'right'
  })
  seriesKeys.forEach((key) => {
    if (unitOf(key) === undefined) axisByName[key] = 'left'
  })

  return {
    axisByName,
    omitted: seriesKeys.filter((key) => !(key in axisByName)),
  }
}

/**
 * {@link assignSeriesAxes} for a series breakdown, taking each series' unit from a
 * sample of its own values. The single place the wire data's units drive the axis
 * split, so the chart bodies and the legend always agree on it.
 */
export const seriesAxisAssignment = (
  data: SeriesRowsData,
  seriesKeys: readonly string[],
  rightAxisSeries?: readonly string[]
): SeriesAxisAssignment =>
  assignSeriesAxes(
    seriesKeys,
    (key) => {
      const sample = seriesSampleValue(data, key)

      return sample === undefined ? undefined : valueUnitKey(sample)
    },
    rightAxisSeries
  )

/** True when the assignment splits series across both axes. */
export const isDualAxis = ({ axisByName }: SeriesAxisAssignment): boolean =>
  Object.values(axisByName).includes('right')

/**
 * True when the given series carry more than one unit — so any figure summed across
 * them (the backend's per-row `total`) is meaningless, money added to a count, and
 * the charts drop it from the tooltip. A series the response declares but carries no
 * value for is ignored: with no unit of its own it can't make a breakdown mixed.
 */
export const hasMixedUnits = (
  data: SeriesRowsData,
  seriesKeys: readonly string[]
): boolean =>
  new Set(
    seriesKeys
      .map((key) => seriesSampleValue(data, key))
      .filter((value): value is ValueType => value !== undefined)
      .map(valueUnitKey)
  ).size > 1

/**
 * One chart record per time bucket, keyed by the row's `key` (an ISO timestamp the
 * x-axis formatter renders), with a numeric value per series (0 when absent for
 * that bucket). A flat `rows` timeseries puts its single value under `value`.
 */
export const timeseriesChartData = (
  data: SeriesRowsData | RowsData
): Record<string, unknown>[] => {
  if (data.type === 'seriesRows') {
    return data.rows.map((row) => {
      const record: Record<string, unknown> = {
        [CATEGORY_KEY]: row.key,
        [TOTAL_KEY]: valueAmount(row.total),
      }

      data.series.forEach((series) => {
        record[series.key] = 0
      })
      row.values.forEach((value) => {
        record[value.series] = valueAmount(value.value)
      })

      return record
    })
  }

  return data.rows.map((row) => ({
    [CATEGORY_KEY]: row.key,
    value: valueAmount(row.value),
  }))
}

/**
 * A representative row total, mirroring the shapes {@link timeseriesChartData} bakes
 * a {@link TOTAL_KEY} into. Its value type gives the total its own unit, so a chart
 * mixing units (a count series against a money one) formats the bucket total
 * correctly instead of inheriting whichever series renders first.
 */
export const rowTotalSample = (data: MetricData): ValueType | undefined =>
  data.type === 'seriesRows' ? data.rows[0]?.total : undefined

// ── rows (donut) ─────────────────────────────────────────────────────────────

export type DonutDatum = {
  key: string
  label: string
  amount: number
  color: string
}

export const rowsDonutData = (
  data: RowsData,
  colourMap: InsightsColourMap
): DonutDatum[] =>
  data.rows.map((row, index) => ({
    key: row.key,
    label: row.label,
    amount: valueAmount(row.value),
    color: resolveColour(row.key, index, colourMap),
  }))

const firstRowValue = (data: RowsData): ValueType | undefined =>
  data.rows[0]?.value

/** One chart row per flat row, for rendering a `rows` breakdown as a single-series bar. */
export const rowsBarData = (data: RowsData): Record<string, unknown>[] =>
  data.rows.map((row) => ({
    [CATEGORY_KEY]: row.label,
    value: valueAmount(row.value),
  }))

/** Donut slices from a series breakdown's row totals (one slice per row). */
export const seriesTotalsDonutData = (
  data: SeriesRowsData,
  colourMap: InsightsColourMap
): DonutDatum[] =>
  data.rows.map((row, index) => ({
    key: row.key,
    label: row.label,
    amount: valueAmount(row.total),
    color: resolveColour(row.key, index, colourMap),
  }))

/** Legend for a donut of a series breakdown's row totals (one entry per row). */
export const seriesTotalsLegendItems = (
  data: SeriesRowsData,
  colourMap: InsightsColourMap
): LegendItem[] =>
  seriesTotalsDonutData(data, colourMap).map(({ key, label, color }) => ({
    key,
    label,
    color,
  }))

// ── shared ─────────────────────────────────────────────────────────────────

/** A representative value to seed the currency-aware tick/tooltip formatter. */
export const sampleValue = (data: MetricData): ValueType | undefined => {
  switch (data.type) {
    case 'scalar':
      return data.value
    case 'rows':
      return firstRowValue(data)
    case 'seriesRows':
      return firstSeriesValue(data)
  }
}

/** Legend items for whichever breakdown the data represents (empty for scalars). */
export const legendItems = (
  data: MetricData,
  colourMap: InsightsColourMap
): LegendItem[] => {
  if (data.type === 'seriesRows') {
    const present = presentSeriesKeys(data)

    return seriesViews(data, colourMap)
      .filter((view) => present.has(view.name))
      .map(({ name, label, color }) => ({ key: name, label, color }))
  }

  if (data.type === 'rows') {
    return rowsDonutData(data, colourMap).map(({ key, label, color }) => ({
      key,
      label,
      color,
    }))
  }

  return []
}

/**
 * {@link legendItems} with the series the axis split couldn't plot marked `muted`,
 * so a breakdown carrying three-plus units still *names* what isn't on the chart
 * instead of dropping it without trace. Used by every chart that can split series
 * across two axes (bar, line, area, combo); the donut always plots the whole
 * breakdown on one scale, so it uses {@link legendItems} directly.
 */
export const axisAwareLegendItems = (
  data: MetricData,
  colourMap: InsightsColourMap,
  {
    rightAxisSeries,
    seriesOrder = [],
  }: {
    rightAxisSeries?: readonly string[]
    /**
     * The domain's canonical series order — the same one the chart bodies apply.
     * Required for agreement: equally-sized unit groups are broken by declared
     * order, so deriving the split from the raw response order here while the body
     * derives it from the ordered one could mute a series the chart actually plots
     * (and leave the one it dropped unmarked).
     */
    seriesOrder?: readonly string[]
  } = {}
): LegendItem[] => {
  const items = legendItems(data, colourMap)

  if (data.type !== 'seriesRows') return items

  const { omitted } = seriesAxisAssignment(
    data,
    orderByKeys(data.series, seriesOrder, (series) => series.key).map(
      (series) => series.key
    ),
    rightAxisSeries
  )

  if (omitted.length === 0) return items

  return items.map((item) =>
    omitted.includes(item.key) ? { ...item, muted: true } : item
  )
}
